import { jsx as _jsx } from "react/jsx-runtime";
import { useLocale } from '@aviv-unified-frontend/runtime/i18n';
import { CustomTrans } from '@/components/CustomTrans';
import { Faq } from '@/components/Faq/Faq';
import { formatCurrency } from '@/helpers/format/formatCurrency';
import { formatDate } from '@/helpers/format/formatDate';
import { usePageLayoutContext } from '@/hooks/usePageLayoutContext/usePageLayoutContext';
import { useShellTranslation } from '@/hooks/useShellTranslation/useShellTranslation';
export const RealEstatePricesFaq = ({ pricesFaq }) => {
    const { t } = useShellTranslation();
    const { pricePageProps } = usePageLayoutContext();
    const { language } = useLocale();
    if (!pricesFaq || !pricesFaq.lastYearPrice) {
        return null;
    }
    const placeName = pricePageProps.pricePageContext.place.name.full || '';
    const averagePrice = formatCurrency(pricePageProps.pricePageContext.prices?.sell?.hybrid?.average, language);
    const housesPrice = formatCurrency(pricePageProps.pricePageContext.prices?.sell?.house?.average, language);
    const apartmentsPrice = formatCurrency(pricePageProps.pricePageContext.prices?.sell?.apartment?.average, language);
    const formattedDate = formatDate({
        date: new Date().toString(),
        locale: language,
        shortFormat: false,
    });
    const lastYearAveragePrice = formatCurrency(pricesFaq.lastYearPrice.hybrid?.average, language);
    // If we have houses and apartments prices, we can display the long texts
    const mostExpensiveLong = pricesFaq.mostExpensiveChild?.prices?.house !== undefined &&
        pricesFaq.mostExpensiveChild?.prices?.apartment !== undefined;
    // If we have houses and apartments prices, we can display the long texts
    const cheapestLong = pricesFaq.cheapestChild?.prices?.house !== undefined &&
        pricesFaq.cheapestChild?.prices?.apartment !== undefined;
    const items = [
        {
            question: t('faq_block.info_view.subtitle_1', {
                replace: { place: placeName },
                unsafe_escapeValue: false,
            }),
            answer: t('faq_block.info_view.description_1', {
                replace: {
                    place: placeName,
                    house_price: housesPrice,
                    date: formattedDate,
                },
                unsafe_escapeValue: false,
            }),
        },
        {
            question: t('faq_block.info_view.subtitle_2', {
                replace: { place: placeName },
                unsafe_escapeValue: false,
            }),
            answer: t('faq_block.info_view.description_2', {
                replace: {
                    place: placeName,
                    apartment_price: apartmentsPrice,
                    date: formattedDate,
                },
                unsafe_escapeValue: false,
            }),
        },
        {
            question: t('faq_block.info_view.subtitle_3', {
                replace: { place: placeName },
                unsafe_escapeValue: false,
            }),
            answer: t('faq_block.info_view.description_3', {
                replace: {
                    place: placeName,
                    property_price: averagePrice,
                    property_price_last_year: lastYearAveragePrice,
                    date: formattedDate,
                },
                unsafe_escapeValue: false,
            }),
        },
        ...(pricesFaq.mostExpensiveChild && pricesFaq.cheapestChild
            ? [
                {
                    question: t('faq_block.info_view.subtitle_4', {
                        replace: { place: placeName },
                        unsafe_escapeValue: false,
                    }),
                    answer: (_jsx(CustomTrans, { i18nKey: mostExpensiveLong
                            ? 'faq_block.info_view.description_4_long'
                            : 'faq_block.info_view.description_4_short', values: {
                            place: placeName,
                            most_expensive_neighborhood: pricesFaq.mostExpensiveChild?.name || '',
                            property_price: formatCurrency(pricesFaq.mostExpensiveChild?.prices?.hybrid?.average, language),
                            apartment_price: formatCurrency(pricesFaq.mostExpensiveChild?.prices?.apartment?.average, language),
                            house_price: formatCurrency(pricesFaq.mostExpensiveChild?.prices?.house?.average, language),
                        }, linksUrl: {
                            link1: {
                                href: pricesFaq.mostExpensiveChild?.url || '',
                            },
                        } })),
                },
                {
                    question: t('faq_block.info_view.subtitle_5', {
                        replace: { place: placeName },
                        unsafe_escapeValue: false,
                    }),
                    answer: (_jsx(CustomTrans, { i18nKey: cheapestLong
                            ? 'faq_block.info_view.description_5_long'
                            : 'faq_block.info_view.description_5_short', values: {
                            place: placeName,
                            cheapest_neighborhood: pricesFaq.cheapestChild?.name || '',
                            property_price: formatCurrency(pricesFaq.cheapestChild?.prices?.hybrid?.average, language),
                            apartment_price: formatCurrency(pricesFaq.cheapestChild?.prices?.apartment?.average, language),
                            house_price: formatCurrency(pricesFaq.cheapestChild?.prices?.house?.average, language),
                        }, linksUrl: {
                            link2: {
                                href: pricesFaq.cheapestChild?.url || '',
                            },
                        } })),
                },
            ]
            : []),
    ];
    return (_jsx(Faq, { blockTitle: t('faq_block.info_view.title', {
            replace: { place: placeName },
            unsafe_escapeValue: false,
        }), items: items }));
};
